import React, { useCallback, useId, useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertCircle } from "lucide-react";
import { inputCls, inputErrCls, labelCls } from "./ui";

/* ---------- validators ---------- */
export const isBlank = (v) => v === undefined || v === null || String(v).trim() === "";
export const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(v).trim());
export const isMobile = (v) => /^[6-9]\d{9}$/.test(String(v).replace(/[\s-]/g, ""));
export const isDomain = (v) => /^(?!-)([a-z0-9-]{1,63}(?<!-)\.)+[a-z]{2,}$/i.test(String(v).trim());
export const isUrl = (v) => { try { const u = new URL(String(v).trim()); return u.protocol === "http:" || u.protocol === "https:"; } catch { return false; } };
export const isGst = (v) => /^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(String(v).trim().toUpperCase());
export const toNum = (v) => (isBlank(v) ? NaN : Number(v));

/* ---------- form state with "show errors after touch or submit" ---------- */
export function useValidatedForm(initial, validate) {
  const [values, setValues] = useState(initial);
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const errors = useMemo(() => validate(values), [values, validate]);

  const set = useCallback((k, v) => setValues((p) => ({ ...p, [k]: v })), []);
  const bind = (k) => ({
    value: values[k] ?? "",
    onChange: (v) => set(k, v),
    onBlur: () => setTouched((t) => ({ ...t, [k]: true })),
    error: submitted || touched[k] ? errors[k] : undefined,
  });
  const reset = (next = initial) => { setValues(next); setTouched({}); setSubmitted(false); };

  // Returns true when valid. `only` limits the check to some fields (e.g. one wizard step).
  const check = (only) => {
    const keys = Object.keys(errors).filter((k) => !only || only.includes(k));
    if (only) setTouched((t) => ({ ...t, ...Object.fromEntries(only.map((k) => [k, true])) }));
    else setSubmitted(true);
    if (keys.length) {
      toast.error(keys.length === 1 ? errors[keys[0]] : `Please fix ${keys.length} highlighted fields to continue.`);
      requestAnimationFrame(() => document.querySelector('[aria-invalid="true"]')?.focus());
      return false;
    }
    return true;
  };
  return { values, set, setValues, bind, errors, check, reset, hasErrors: Object.keys(errors).length > 0 };
}

/* ---------- fields ---------- */
export function Field({
  label, required, type = "text", value, onChange, onBlur, error, hint, t, options, placeholder, rows = 3,
  maxLength, prefix, suffix, className = "", disabled, min, max, step, autoComplete, inputClassName = "", list, children,
}) {
  const id = useId();
  const errId = `${id}-err`;
  const common = {
    id, value, disabled, onBlur, list, "data-testid": t, "aria-invalid": error ? "true" : "false",
    "aria-describedby": error ? errId : undefined,
  };
  const cls = `${inputCls} ${error ? inputErrCls : ""} ${inputClassName}`;

  let control;
  if (type === "select") {
    control = (
      <select {...common} onChange={(e) => onChange(e.target.value)} className={cls}>
        <option value="">{placeholder || "Select…"}</option>
        {(options || []).map((o) => {
          const val = typeof o === "object" ? o.value : o;
          const lab = typeof o === "object" ? o.label : o;
          return <option key={val} value={val}>{lab}</option>;
        })}
      </select>
    );
  } else if (type === "textarea") {
    control = (
      <textarea {...common} rows={rows} maxLength={maxLength} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
        className={`${cls} !h-auto py-2 resize-y`} />
    );
  } else if (prefix || suffix) {
    control = (
      <div className={`flex items-stretch rounded-lg border bg-white transition focus-within:ring-2 ${error ? "border-red-500 focus-within:ring-red-500/15" : "border-slate-300 focus-within:border-blue-600 focus-within:ring-blue-600/15"}`}>
        {prefix && <span className="px-2.5 flex items-center text-[13px] text-slate-500 bg-slate-50 border-r border-slate-200 rounded-l-lg">{prefix}</span>}
        <input {...common} type={type} min={min} max={max} step={step} maxLength={maxLength} placeholder={placeholder} autoComplete={autoComplete}
          onChange={(e) => onChange(e.target.value)} className="flex-1 min-w-0 h-[34px] px-3 text-[13px] text-neutral-900 placeholder:text-slate-400 bg-transparent outline-none disabled:text-slate-400" />
        {suffix && <span className="px-2.5 flex items-center text-[13px] text-slate-500 bg-slate-50 border-l border-slate-200 rounded-r-lg">{suffix}</span>}
      </div>
    );
  } else {
    control = (
      <input {...common} type={type} min={min} max={max} step={step} maxLength={maxLength} placeholder={placeholder} autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)} className={cls} />
    );
  }

  return (
    <div className={className}>
      {label && (
        <label htmlFor={id} className={labelCls}>
          {label}{required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      {control}
      {children}
      {error ? (
        <p id={errId} role="alert" className="mt-1 text-[11px] text-red-600 flex items-start gap-1"><AlertCircle size={12} className="mt-px shrink-0" />{error}</p>
      ) : (hint || (type === "textarea" && maxLength)) ? (
        <p className="mt-1 text-[11px] text-slate-500 flex justify-between gap-2">
          <span>{hint}</span>
          {type === "textarea" && maxLength ? <span>{String(value || "").length}/{maxLength}</span> : null}
        </p>
      ) : null}
    </div>
  );
}

export function CheckField({ label, hint, checked, onChange, t, className = "" }) {
  return (
    <label className={`flex items-start gap-2 cursor-pointer select-none ${className}`}>
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} data-testid={t} className="mt-0.5 w-4 h-4 accent-blue-600" />
      <span><span className="text-[13px] text-neutral-800 font-medium">{label}</span>{hint && <span className="block text-[11px] text-slate-500">{hint}</span>}</span>
    </label>
  );
}
